import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { html, LitElement, nothing } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { CSEModal } from '@cloudsuite/mosaic-elements'
import { modalStyles } from 'components/ordering/quotation/components/quotation.style.js'
import { UpdateQuotationPayload } from 'components/ordering/quotation/api/requests/update-quotation.js'
import { QuotationDTO } from 'components/ordering/quotation/api/dtos/quotation-dto.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { WINDOW_LOCATION, WindowLocation } from '@cloudsuite/mosaic-shared/scripts/window-location.js'
import { redirectToPath } from '@cloudsuite/mosaic-shared/scripts/base-url.js'
import { consume } from '@lit/context'
import {
  activeQuotationContext,
  deleteOpenQuotationContext,
  deleteQuotationContext,
  NotFetchedYet,
  quotationActionErrorContext,
  reopenDraftQuotationContext,
  reopenQuotationContext,
  updateQuotationContext
} from 'components/ordering/quotation/components/context/quotation-context.js'
import { NotFoundError, QuotationError } from 'components/ordering/quotation/api/dtos/error.js'
import { isQuotationDTO } from 'components/ordering/quotation/components/is-quotation-dto.js'

@customElement('csc-quotation-modal')
export class CSCQuotationModal extends LitElement {
  static override styles = [
    modalStyles
  ]

  @inject(WINDOW_LOCATION)
  private windowLocation!: WindowLocation

  @consume({ context: activeQuotationContext, subscribe: true })
  private quotation!: QuotationDTO | NotFoundError | NotFetchedYet

  @property({ type: String, attribute: 'target-id' })
  target: string = 'default'

  @property({ type: String, attribute: 'type' })
  type!: 'delete' | 'reopen' | 'decline'

  @query('cse-modal')
  private _modal!: CSEModal | null | undefined

  @query('cse-textarea[id="decline"]')
  private _declineTextarea!: HTMLTextAreaElement | null | undefined

  open() {
    if (this._modal) {
      this._modal.open().then(() => nothing)
    }
  }

  close() {
    if (this._modal) {
      this._modal.close().then(() => nothing)
    }
  }

  @consume({ context: quotationActionErrorContext, subscribe: true })
  private actionError!: QuotationError | null

  @consume({ context: deleteQuotationContext })
  private deleteQuotation!: (quotationId: number) => Promise<void>

  async delete(): Promise<void> {
    this.close()

    if (!isQuotationDTO(this.quotation)) return
    await this.deleteQuotation(this.quotation.id)
    if (this.actionError) return

    await redirectToPath(this.windowLocation, 'account/quotation/')
  }

  @consume({ context: deleteOpenQuotationContext })
  private deleteOpenQuotation!: () => Promise<void>

  @consume({ context: reopenQuotationContext })
  private reopenQuotation!: (quotationId: number) => Promise<void>

  @consume({ context: reopenDraftQuotationContext })
  private reopenDraftQuotation!: (quotationId: number) => Promise<void>

  private async reopen() {
    this.close()

    if (!isQuotationDTO(this.quotation)) {
      throw new Error('Quotation is not valid')
    }

    await this.deleteOpenQuotation()
    if (this.actionError) return

    if (this.quotation.status === 'draft') {
      await this.reopenDraftQuotation(this.quotation.id)
      return
    }

    await this.reopenQuotation(this.quotation.id)
  }

  @consume({ context: updateQuotationContext })
  private updateQuotation!: (payload: UpdateQuotationPayload) => Promise<void>

  private async decline() {
    this.close()

    if (!isQuotationDTO(this.quotation)) {
      throw new Error('Quotation is not valid')
    }

    await this.updateQuotation({
      quotationId: this.quotation.id,
      status: 'declined',
      reasonOfDecline: this._declineTextarea?.value || ''
    })
  }

  private deleteModal() {
    return html`
      <cse-modal id="${this.target}" size="default" variant="error">
        <div slot="header">${t('delete_header', {
          ns: 'csc-quotation-modal',
          default: 'Delete quotation'
        })}
        </div>
        <cse-icon slot="icon" icon-name="trash-can" icon-style="light" size="xl"></cse-icon>
        <cse-text>
          ${t('delete_body', {
            ns: 'csc-quotation-modal',
            default: 'You are about to delete the quotation. This cannot be undone, do you want to continue?'
          })}
        </cse-text>

        <cse-button slot="footer"
                    @click="${() => this.close()}"
                    size="small"
                    fit="fill"
        >
          ${t('delete_cancel', {
            ns: 'csc-quotation-modal',
            default: 'No, cancel'
          })}
        </cse-button>

        <cse-button slot="footer"
                    @click="${() => this.delete()}"
                    size="small"
                    fit="fill"
        >
          ${t('delete', {
            ns: 'csc-quotation-modal',
            default: 'Yes, delete'
          })}
        </cse-button>
      </cse-modal>`
  }

  private reopenModal() {
    return html`
      <cse-modal id="${this.target}" size="default" variant="warning">
        <div slot="header">${t('reopen_header', {
          ns: 'csc-quotation-modal',
          default: 'You have a current active quotation'
        })}
        </div>
        <cse-icon slot="icon" icon-name="exclamation-triangle" icon-style="light" size="xl"></cse-icon>
        <cse-text>
          ${t('reopen_body', {
            ns: 'csc-quotation-modal',
            default: 'This will replace the current active quotation. Do you want to continue?'
          })}
        </cse-text>

        <cse-button slot="footer"
                    @click="${() => this.close()}"
                    size="small"
                    fit="fill"
        >
          ${t('reopen_cancel', {
            ns: 'csc-quotation-modal',
            default: 'No, cancel'
          })}
        </cse-button>

        <cse-button slot="footer"
                    @click="${() => this.reopen()}"
                    size="small"
                    fit="fill"
        >
          ${t('reopen', {
            ns: 'csc-quotation-modal',
            default: 'Yes, continue'
          })}
        </cse-button>
      </cse-modal>`
  }

  private declineModal() {
    return html`
      <cse-modal id="${this.target}" size="default" variant="error">
        <div slot="header">${t('decline_header', {
          ns: 'csc-quotation-modal',
          default: 'Decline quotation'
        })}
        </div>
        <cse-icon slot="icon" icon-name="trash-can" icon-style="light" size="xl"></cse-icon>
        <cse-text>
          ${t('decline_body', {
            ns: 'csc-quotation-modal',
            default: 'You are about to decline the quotation. Please give a reason why you are declining'
          })}
        </cse-text>

        <cse-label for="decline">
          ${t('decline_label', {
            ns: 'csc-quotation-modal',
            default: 'Reason'
          })}

        </cse-label>
        <cse-textarea
          id="decline"
          maxlength="100"
          placeholder="${t(
            'decline_placeholder',
            {
              ns: 'csc-quotation-modal',
              default: 'Some text'
            }
          )}"
        ></cse-textarea>

        <cse-button slot="footer"
                    @click="${() => this.close()}"
                    size="small"
                    fit="fill"
        >
          ${t('decline_cancel', {
            ns: 'csc-quotation-modal',
            default: 'Cancel'
          })}
        </cse-button>
        <cse-button slot="footer"
                    @click="${() => this.decline()}"
                    size="small"
                    fit="fill"
                    variant="secondary"
        >
          ${t('decline', {
            ns: 'csc-quotation-modal',
            default: 'Decline quotation'
          })}
        </cse-button>
      </cse-modal>`
  }

  override render() {
    switch (this.type) {
      case 'delete':
        return this.deleteModal()
      case 'reopen':
        return this.reopenModal()
      case 'decline':
        return this.declineModal()
      default:
        return nothing
    }
  }
}
